import { useAuth } from "./useAuth";

/**
 * Rol de realm que marca a un usuario como aprobado. Debe coincidir con
 * SecurityConfig.REQUIRED_ROLE del backend. El registro en Keycloak es
 * abierto; el admin aprueba asignando este rol desde la UI de Keycloak.
 */
const REQUIRED_ROLE = "app-user";

/**
 * Bloquea el renderizado de sus children hasta que la auth esté resuelta.
 *
 * Estados visuales:
 *   initializing  → "Verificando sesión..." (mientras keycloak.init() corre)
 *   error         → Mensaje de error + botón de reintentar
 *   unauthenticated → Pantalla de bienvenida + botón "Iniciar sesión"
 *   authenticated sin rol → "Cuenta pendiente de aprobación" (gate de acceso)
 *   authenticated con rol → Renderiza children (la app real)
 *
 * El chequeo de rol aquí es solo UX — la defensa real es el backend, que
 * responde 403 si el JWT no trae el rol.
 *
 * Uso:
 *   <AuthProvider>
 *     <LoginGate>
 *       <App />   ← solo se renderiza si el usuario está autenticado y aprobado
 *     </LoginGate>
 *   </AuthProvider>
 */
export function LoginGate({ children }) {
  const { status, error, login, logout, user } = useAuth();

  if (status === "initializing") {
    return (
      <div style={styles.center}>
        <p>🔐 Verificando sesión...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={styles.center}>
        <h2>⚠ Error de autenticación</h2>
        <p style={styles.errorText}>{error}</p>
        <button style={styles.button} onClick={() => window.location.reload()}>
          Reintentar
        </button>
      </div>
    );
  }

  if (status === "unauthenticated") {
    return (
      <div style={styles.center}>
        <h1 style={styles.title}>🛍 Conócelo</h1>
        <p style={styles.subtitle}>
          Investiga antes de comprar. Conoce lo que vas a elegir.
        </p>
        <button style={styles.button} onClick={login}>
          Iniciar sesión
        </button>
      </div>
    );
  }

  // Autenticado pero sin el rol de aprobado → pantalla de espera.
  // Tras la aprobación, "Verificar de nuevo" recarga la página: keycloak-js
  // re-inicializa contra la sesión SSO y obtiene un token fresco que ya
  // incluye el rol recién asignado.
  const roles = user?.realm_access?.roles || [];
  if (!roles.includes(REQUIRED_ROLE)) {
    return (
      <div style={styles.center}>
        <h1 style={styles.title}>🛍 Conócelo</h1>
        <h2 style={{ margin: "0 0 8px", color: "#1a1a2e" }}>
          ⏳ Cuenta pendiente de aprobación
        </h2>
        <p style={styles.subtitle}>
          Tu cuenta <strong>{user?.email || user?.preferred_username}</strong>{" "}
          fue creada, pero el acceso a la app se habilita manualmente. Avísale
          al administrador y vuelve a intentar cuando te confirme.
        </p>
        <div style={{ display: "flex", gap: "10px" }}>
          <button style={styles.button} onClick={() => window.location.reload()}>
            Verificar de nuevo
          </button>
          <button style={styles.secondaryButton} onClick={logout}>
            Cerrar sesión
          </button>
        </div>
      </div>
    );
  }

  // status === 'authenticated' y aprobado — pasa los children
  return children;
}

const styles = {
  center: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "sans-serif",
    background: "#f8f7ff",
    padding: "20px",
    textAlign: "center",
  },
  title: {
    fontSize: "32px",
    color: "#1a1a2e",
    margin: "0 0 8px",
  },
  subtitle: {
    fontSize: "16px",
    color: "#555",
    margin: "0 0 32px",
    maxWidth: "400px",
  },
  button: {
    background: "#6c63ff",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "12px 32px",
    fontSize: "15px",
    fontWeight: 500,
    cursor: "pointer",
  },
  secondaryButton: {
    background: "transparent",
    color: "#6c63ff",
    border: "1px solid #6c63ff",
    borderRadius: "8px",
    padding: "12px 32px",
    fontSize: "15px",
    fontWeight: 500,
    cursor: "pointer",
  },
  errorText: {
    color: "#c0392b",
    background: "#fff0f0",
    padding: "12px 16px",
    borderRadius: "8px",
    margin: "12px 0 20px",
    maxWidth: "500px",
  },
};
